// Sugalarm — Cycle 134: Log monthly calendar.
//
// A fresh month-grid reintroduction (the cycle-31 calendar was deleted
// outright in cycle 47; none of that code is reused). Renders an additive
// `?view=calendar` mode on the recent-entries screen: a month-nav row, a
// Sunday-start 7-column grid that flex-fills the content height (no
// scrollbar at 5 or 6 weeks), and per-day markers — a count pill plus one
// dot per kind logged that day, in fixed kind order, using each kind's
// existing color token (`treatmentColor`). Tapping an in-month day drills
// into that day's existing single-day list (the parent sets `?date=`).

import { useMemo } from "react";

import { treatmentColor } from "../../components/glucose/markerGlyphs";
import { localDayKey } from "../../lib/glucose";
import type { Treatment } from "../../lib/types";
import { buildMonthGrid } from "./monthGrid";
import { useMonthTreatments } from "./useMonthTreatments";
import styles from "./LogMonthCalendar.module.css";

type EventType = Treatment["event_type"];

// Fixed marker order (matches the prompt: insulin, carbs, BG, note,
// exercise). Each renders in its existing per-kind color via treatmentColor.
const KIND_ORDER: EventType[] = [
  "insulin",
  "carbs",
  "bg_check",
  "note",
  "exercise",
];

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

interface Props {
  /** Cycle 203: Android-tablet kiosk → glass container + glass day cells +
   *  popped nav buttons. Absent/false → byte-identical to before. */
  androidTablet?: boolean;
  year: number;
  month: number; // 0-11
  todayKey: string;
  floorKey: string;
  /** Active kind filter (null = All) — gates which markers + count show. */
  activeKind: EventType | null;
  canPrev: boolean;
  canNext: boolean;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
  onPickDay: (dayKey: string) => void;
}

export function LogMonthCalendar({
  androidTablet = false,
  year,
  month,
  todayKey,
  floorKey,
  activeKind,
  canPrev,
  canNext,
  onPrevMonth,
  onNextMonth,
  onToday,
  onPickDay,
}: Props) {
  const { byDay, error } = useMonthTreatments(year, month);

  const cells = useMemo(() => buildMonthGrid(year, month).cells, [year, month]);

  const monthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        month: "long",
        year: "numeric",
      }).format(new Date(year, month, 1)),
    [year, month],
  );

  return (
    <div className={`${styles.cal} ${androidTablet ? styles.androidTablet : ""}`}>
      <div className={styles.navRow}>
        <button
          type="button"
          className={styles.navChev}
          aria-label="Previous month"
          disabled={!canPrev}
          onClick={onPrevMonth}
        >
          ‹
        </button>
        <span className={styles.monthLabel}>{monthLabel}</span>
        <button
          type="button"
          className={styles.navChev}
          aria-label="Next month"
          disabled={!canNext}
          onClick={onNextMonth}
        >
          ›
        </button>
        <button type="button" className={styles.todayBtn} onClick={onToday}>
          Today
        </button>
      </div>

      {error ? <div className={styles.error}>{error}</div> : null}

      <div className={styles.weekHead}>
        {WEEKDAYS.map((w) => (
          <span key={w} className={styles.weekCell}>
            {w}
          </span>
        ))}
      </div>

      <div className={styles.grid}>
        {cells.map((cell) => {
          const key = localDayKey(cell);
          const inMonth = cell.getMonth() === month;
          const isToday = key === todayKey;
          const isFuture = key > todayKey;
          const belowFloor = key < floorKey;
          // In-month, not future, not before the 365-day floor → tappable
          // (empty days open an empty day-detail; out-of-month/future/
          // pre-floor are dimmed, inert chrome).
          const tappable = inMonth && !isFuture && !belowFloor;

          const all = byDay.get(key) ?? [];
          const dayRows =
            activeKind == null
              ? all
              : all.filter((t) => t.event_type === activeKind);
          const count = dayRows.length;
          const present = new Set(dayRows.map((t) => t.event_type));

          const cellClass = [
            styles.cell,
            inMonth ? styles.inMonth : styles.outMonth,
            isToday ? styles.today : "",
            tappable ? styles.tappable : "",
          ]
            .filter(Boolean)
            .join(" ");

          const content = (
            <>
              <span className={styles.dayNum}>{cell.getDate()}</span>
              {count > 0 ? (
                <span className={styles.countPill}>{count}</span>
              ) : null}
              {count > 0 ? (
                <span className={styles.dots}>
                  {KIND_ORDER.filter((k) => present.has(k)).map((k) => (
                    <span
                      key={k}
                      className={styles.dot}
                      style={{ background: treatmentColor(k) }}
                    />
                  ))}
                </span>
              ) : null}
            </>
          );

          return tappable ? (
            <button
              key={key}
              type="button"
              className={cellClass}
              aria-label={`${monthLabel} ${cell.getDate()}${
                count > 0 ? `, ${count} entries` : ""
              }`}
              onClick={() => onPickDay(key)}
            >
              {content}
            </button>
          ) : (
            <div key={key} className={cellClass} aria-hidden="true">
              {content}
            </div>
          );
        })}
      </div>
    </div>
  );
}
